/**
 * HTTP client for the Mentor Connect API.
 *
 * *** ONE REFRESH AT A TIME ***
 * A dashboard fires several requests at once. If the access token has expired,
 * every one of them gets a 401 simultaneously. Refreshing per-request would
 * send N refresh calls with the SAME token — and the server implements refresh
 * rotation with reuse detection, so the second one arriving with an
 * already-rotated token looks like a stolen token and **revokes the entire
 * family**, logging the admin out.
 *
 * So a refresh in flight is shared: the first 401 starts it, the rest await the
 * same promise.
 *
 * *** TOKENS LIVE IN localStorage, DELIBERATELY ***
 * The alternative is an httpOnly cookie, which is safer against XSS but needs
 * the API to set cookies and handle CSRF — server changes for a client-side
 * problem. This portal is an internal tool behind a login, its XSS surface is
 * small (React escapes by default and nothing renders raw HTML), and a
 * compromised admin browser is already game over. Revisit if the portal ever
 * renders user-supplied markup.
 */

import type {
  ApiErrorResponse,
  CredentialLoginRequest,
  StudentRegisterResult,
  TokenResponse,
} from '@/types/api'

const API_BASE = '/api/v1'

const ACCESS_TOKEN_KEY = 'mc_admin_access_token'
const REFRESH_TOKEN_KEY = 'mc_admin_refresh_token'
const USER_KEY = 'mc_admin_user'

/*
 * *** THERE IS DELIBERATELY NO APP_KEY HERE ***
 *
 * This portal used to export:
 *
 *     export const APP_KEY =
 *       import.meta.env.VITE_APP_KEY ?? 'mk_dev_sure60_local_testing_key_00001'
 *
 * and send it with every sign-in. That made ONE portal build serve exactly
 * ONE tenant. Worse, no .env ever defined VITE_APP_KEY, so every deployment
 * silently fell back to the S60 development key — and on 2026-09-19 Sure60's
 * admin could not sign in to the production portal, with the login page
 * reporting "Those details did not match" for credentials that were correct.
 *
 * The tenant now comes from the admin's email, resolved server-side. Do not
 * reintroduce a build-time tenant key here: it cannot be right for more than
 * one client at a time, and when it is wrong it fails as a password error.
 */

// --------------------------------------------------------------- error type
/** An error carrying the server's stable code and user-safe message. */
export class ApiError extends Error {
  readonly code: string
  readonly status: number
  readonly details?: Record<string, unknown>

  constructor(
    status: number,
    code: string,
    message: string,
    details?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
  }

  /** True when retrying could plausibly succeed. */
  get isTransient(): boolean {
    return this.status >= 500 || this.status === 429
  }

  /** A slot taken during checkout. Expected under contention, not a failure. */
  get isConflict(): boolean {
    return this.status === 409
  }
}

// ------------------------------------------------------------ token storage
export interface StoredSession {
  accessToken: string
  refreshToken: string
  user: TokenResponse['user']
}

export const tokenStore = {
  get(): StoredSession | null {
    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY)
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    const rawUser = localStorage.getItem(USER_KEY)

    if (!accessToken || !refreshToken || !rawUser) return null

    try {
      return { accessToken, refreshToken, user: JSON.parse(rawUser) }
    } catch {
      // Corrupted storage — treat as signed out rather than crashing at boot.
      tokenStore.clear()
      return null
    }
  },

  set(response: TokenResponse): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, response.access_token)
    localStorage.setItem(REFRESH_TOKEN_KEY, response.refresh_token)
    localStorage.setItem(USER_KEY, JSON.stringify(response.user))
  },

  clear(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  },
}

/**
 * Called when the session is unrecoverable. Set by the auth provider so the
 * client can trigger a logout without importing React.
 */
let onSessionExpired: (() => void) | null = null
export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler
}

// ------------------------------------------------------------------ refresh
/** The single in-flight refresh. See the module docstring. */
let refreshInFlight: Promise<string> | null = null

async function refreshAccessToken(): Promise<string> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const session = tokenStore.get()
    if (!session) throw new ApiError(401, 'NO_SESSION', 'Your session has ended.')

    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: session.refreshToken }),
    })

    if (!response.ok) {
      // The refresh token is dead: expired, rotated, or its family revoked.
      // Nothing to retry — the admin must sign in again.
      tokenStore.clear()
      onSessionExpired?.()
      throw new ApiError(401, 'SESSION_EXPIRED', 'Please sign in again.')
    }

    const tokens: TokenResponse = await response.json()
    tokenStore.set(tokens)
    return tokens.access_token
  })()

  try {
    return await refreshInFlight
  } finally {
    // Cleared in `finally` so a FAILED refresh does not poison every later
    // request with the same rejected promise.
    refreshInFlight = null
  }
}

// ------------------------------------------------------------------ request
interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  body?: unknown
  /** Query parameters. Null and undefined values are dropped. */
  params?: Record<string, string | number | boolean | null | undefined>
  /** Skips the Authorization header. Login and refresh only. */
  anonymous?: boolean
  signal?: AbortSignal
  /**
   * Extra request headers.
   *
   * Added for `X-Join-Mode`, which asks the join route to answer with a
   * body instead of a 302 that a browser cannot read. Never for
   * Authorization — that is managed here so the refresh-retry works.
   */
  headers?: Record<string, string>
}

function buildUrl(
  path: string,
  params?: RequestOptions['params'],
): string {
  const url = `${API_BASE}${path}`
  if (!params) return url

  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    // Empty strings are dropped too: `?search=` is noise, and some filters
    // treat an empty value differently from an absent one.
    if (value === null || value === undefined || value === '') continue
    search.set(key, String(value))
  }

  const query = search.toString()
  return query ? `${url}?${query}` : url
}

async function parseError(response: Response): Promise<ApiError> {
  let code = 'UNKNOWN_ERROR'
  let message = 'Something went wrong. Please try again.'
  let details: Record<string, unknown> | undefined

  try {
    const body = (await response.json()) as ApiErrorResponse | unknown

    if (body && typeof body === 'object' && 'error' in body) {
      const envelope = (body as ApiErrorResponse).error
      code = envelope.code ?? code
      // The server writes `message` for end users, so render it directly.
      message = envelope.message ?? message
      details = envelope.details
    } else if (body && typeof body === 'object' && 'detail' in body) {
      // FastAPI's own 422 shape, which bypasses our envelope.
      const detail = (body as { detail: unknown }).detail
      code = 'VALIDATION_ERROR'
      message = Array.isArray(detail)
        ? formatValidationErrors(detail)
        : String(detail)
    }
  } catch {
    // A non-JSON body (a proxy error page, say). Keep the generic message.
  }

  return new ApiError(response.status, code, message, details)
}

/** Turn FastAPI's validation array into one readable sentence. */
function formatValidationErrors(errors: unknown[]): string {
  const parts = errors.slice(0, 3).map((entry) => {
    if (typeof entry !== 'object' || entry === null) return 'Invalid input'
    const { loc, msg } = entry as { loc?: unknown[]; msg?: string }
    // `loc` is ["body", "field_name"] — the last element is the field.
    const field = Array.isArray(loc) ? String(loc[loc.length - 1]) : ''
    return field ? `${humanise(field)}: ${msg ?? 'invalid'}` : (msg ?? 'Invalid input')
  })
  return parts.join('. ')
}

function humanise(field: string): string {
  return field.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase())
}

/**
 * One request, with the 401-refresh-retry the JSON path has.
 *
 * *** THE BODY IS NOT TOUCHED ***
 * `request` below always JSON-stringifies and always sets a JSON content
 * type, which is wrong for a file upload and for a binary download. This
 * sends whatever it is given and returns the raw `Response`, so neither
 * case has to duplicate the token handling and silently drift from it.
 */
async function rawRequest(
  path: string,
  init: { method: string; body?: BodyInit; headers?: Record<string, string> },
): Promise<Response> {
  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { ...init.headers }
    /*
      Deliberately NO Content-Type for FormData: the browser must set it
      itself, because the multipart boundary is generated per request and a
      hand-written header would not match the body.
    */
    if (token) headers.Authorization = `Bearer ${token}`
    return fetch(buildUrl(path), { method: init.method, headers, body: init.body })
  }

  const session = tokenStore.get()
  let response = await send(session?.accessToken ?? null)

  if (response.status === 401 && session) {
    try {
      const freshToken = await refreshAccessToken()
      response = await send(freshToken)
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError(401, 'SESSION_EXPIRED', 'Please sign in again.')
    }
  }

  if (!response.ok) throw await parseError(response)
  return response
}

/**
 * Upload one file to a chat thread.
 *
 * Two steps by design on the server: this returns an `attachment_id`, and
 * the caller then sends a message referencing it. A failed upload therefore
 * never leaves a broken message in the conversation.
 */
export async function uploadChatAttachment(
  threadId: string,
  file: File,
): Promise<import('@/types/api').UploadedAttachment> {
  const form = new FormData()
  form.append('file', file, file.name)
  const response = await rawRequest(
    `/chat/threads/${threadId}/attachments`,
    { method: 'POST', body: form },
  )
  return (await response.json()) as import('@/types/api').UploadedAttachment
}

/**
 * Fetch an attachment's bytes as an object URL.
 *
 * *** NOT THE SIGNED URL ***
 * `GET /chat/attachments/{id}` returns a short-lived link to blob storage,
 * which is the right thing for a CDN — but the local provider has no CDN,
 * and a cross-origin link cannot carry the Bearer token. `/content`
 * re-authorizes the caller and streams the file, which is what the mobile
 * app uses too.
 *
 * The caller MUST revoke the returned URL, or the blob is held until the
 * tab closes.
 */
export async function fetchAttachmentBlobUrl(
  attachmentId: string,
): Promise<string> {
  const response = await rawRequest(
    `/chat/attachments/${attachmentId}/content`,
    { method: 'GET' },
  )
  return URL.createObjectURL(await response.blob())
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    params,
    anonymous = false,
    signal,
    headers: extraHeaders,
  } = options

  const send = async (token: string | null): Promise<Response> => {
    const headers: Record<string, string> = { ...extraHeaders }
    if (body !== undefined) headers['Content-Type'] = 'application/json'
    // Last, so a caller cannot override the managed Authorization header.
    if (token) headers.Authorization = `Bearer ${token}`

    return fetch(buildUrl(path, params), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal,
    })
  }

  const session = anonymous ? null : tokenStore.get()
  let response = await send(session?.accessToken ?? null)

  // *** 401 MEANS "TRY REFRESHING", ONCE ***
  // Not retried for anonymous calls (a failed login is not a stale token), and
  // never retried twice — a second 401 after a fresh token is a real
  // authorization failure, not an expiry.
  if (response.status === 401 && !anonymous && session) {
    try {
      const freshToken = await refreshAccessToken()
      response = await send(freshToken)
    } catch (error) {
      throw error instanceof ApiError
        ? error
        : new ApiError(401, 'SESSION_EXPIRED', 'Please sign in again.')
    }
  }

  if (!response.ok) throw await parseError(response)

  // 204 and other empty responses.
  if (response.status === 204) return undefined as T
  const text = await response.text()
  if (!text) return undefined as T

  return JSON.parse(text) as T
}

// -------------------------------------------------------------- public API
export const api = {
  get: <T>(path: string, params?: RequestOptions['params'], signal?: AbortSignal) =>
    request<T>(path, { method: 'GET', params, signal }),

  post: <T>(path: string, body?: unknown, params?: RequestOptions['params']) =>
    request<T>(path, { method: 'POST', body, params }),

  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body }),

  /*
    `params` because some DELETEs identify their target by range rather
    than by id — a mentor's vacation is stored one row per day and is
    removed by `?start_date=&end_date=`, with no id to put in the path.
  */
  delete: <T>(path: string, params?: RequestOptions['params']) =>
    request<T>(path, { method: 'DELETE', params }),
}

/**
 * Open a booking's meeting.
 *
 * *** THE SERVER REDIRECTS; WE NEVER HOLD THE URL ***
 * `GET /bookings/{id}/join` validates identity, status, readiness and the
 * time window, logs the attempt, then 302s to the conference. The link is
 * deliberately absent from every response body, so it cannot be scraped
 * from API traffic, cached, or forwarded. On the free tier — where Google
 * cannot restrict who joins — that is the only control there is.
 *
 * *** WHY A BROWSER CANNOT JUST FOLLOW THE 302 ***
 * Two things block it, and each alone is enough:
 *
 * 1. The route needs a Bearer token, and the browser sends no such header
 *    on a top-level navigation — so `window.location = '/…/join'` 401s.
 * 2. Fetching it with the header and NOT following the redirect gives an
 *    opaque response: a cross-origin 302 hides every header including
 *    `Location`, by design. Letting fetch follow it instead sends the
 *    request on to Google, which answers HTML.
 *
 * A native client has neither problem, which is why the mobile app reads
 * `Location` directly and this cannot.
 *
 * So the web asks for the same answer as a body, with `X-Join-Mode: json`.
 * Every check still runs server-side — identity, booking status, meeting
 * readiness, the time window — and the attempt is logged either way. The
 * link is still absent from every list and detail response; it is returned
 * only here, to the one person who just passed all of it.
 *
 * Returns the URL rather than navigating, so the caller decides between a
 * new tab and this one, and a refusal is an exception rather than a blank
 * page.
 */
export async function resolveMeetingUrl(bookingId: string): Promise<string> {
  const { join_url } = await request<{ join_url: string }>(
    `/bookings/${bookingId}/join`,
    { method: 'GET', headers: { 'X-Join-Mode': 'json' } },
  )
  return join_url
}

// ------------------------------------------------------------------- auth
export const authApi = {
  /**
   * Sign in a tenant admin.
   *
   * *** NO APP KEY: THE SERVER RESOLVES THE TENANT FROM THE EMAIL ***
   * This portal is a SINGLE deployment serving every client, so it cannot
   * know which tenant the admin belongs to until they identify themselves.
   *
   * It used to send a build-time key, which meant one portal build could
   * only ever serve one tenant. On 2026-09-19 Sure60's admin could not sign
   * in at all: the deployed bundle carried S60's key, so the server searched
   * S60's users for a Sure60 account and correctly found nothing.
   *
   * Nothing is weakened by omitting it. The key named a tenant; it never
   * granted access. The password check and the server-signed JWT decide
   * authorization, exactly as before.
   */
  async login(email: string, password: string): Promise<TokenResponse> {
    const payload: CredentialLoginRequest = {
      email,
      password,
    }
    const tokens = await request<TokenResponse>('/auth/admin/login', {
      method: 'POST',
      body: payload,
      anonymous: true,
    })
    tokenStore.set(tokens)
    return tokens
  },

  /**
   * Sign in a mentor. Email and password, like an admin.
   *
   * No app key, for the same reason the admin login sends none: this portal
   * is one deployment serving every client, and the server resolves the
   * tenant from the email.
   */
  async mentorLogin(
    email: string,
    password: string,
    tenantSlug: string,
  ): Promise<TokenResponse> {
    /*
      *** THE SLUG IS REQUIRED, NOT OPTIONAL ***
      A mentor signs in only from their institute's own link. The server has
      no email lookup for mentors — deliberately, because the same address
      can be a mentor at two institutes and guessing between them would sign
      someone into the wrong client's data.
    */
    const tokens = await request<TokenResponse>('/auth/mentor/login', {
      method: 'POST',
      body: { email, password, tenant_slug: tenantSlug },
      anonymous: true,
    })
    tokenStore.set(tokens)
    return tokens
  },

  /**
   * Ask for a student's one-time code.
   *
   * *** THE SLUG NAMES THE INSTITUTE ***
   * A phone number cannot: the same number could exist in two tenants, and
   * this build carries no app key. The slug comes from the URL the student
   * arrived on, and is already public — it is in the merchant page address
   * the payment gateway requires.
   *
   * Returns nothing useful on purpose. The server answers identically
   * whether or not the number is registered, so that this cannot be used to
   * discover who has an account.
   */
  /**
   * Create a student account.
   *
   * *** NO SESSION COMES BACK ***
   * The account is created PENDING_VERIFICATION and stays unusable until
   * BOTH challenges are answered: an OTP to the phone, and a code to the
   * email. Two independent proofs, both required — a verified phone says
   * nothing about whether the address is real, and the meeting invite goes
   * to the email, so an unverified one means a student who cannot attend
   * the session they paid for.
   *
   * Named by slug for the same reason sign-in is: one portal deployment
   * serves every client and carries no app key.
   */
  async registerStudent(
    tenantSlug: string,
    fullName: string,
    email: string,
    phone: string,
  ): Promise<StudentRegisterResult> {
    return request<StudentRegisterResult>('/auth/student/register', {
      method: 'POST',
      body: {
        tenant_slug: tenantSlug,
        full_name: fullName,
        email,
        phone,
      },
      anonymous: true,
    })
  },

  /**
   * Confirm the emailed 6-digit code.
   *
   * *** AUTHENTICATED, AND THAT IS NOT AN OVERSIGHT ***
   * `/auth/verify-email` exists but is a different mechanism — it consumes a
   * long emailed LINK token, minimum 16 characters, and registration does
   * not issue one. Registration emails a 6-digit code, and the endpoint that
   * consumes it identifies the student from their session.
   *
   * That works because signing in with the phone OTP succeeds while the
   * account is still PENDING_VERIFICATION: only SUSPENDED is refused. So the
   * order is register → OTP sign-in → enter the email code, and the account
   * is promoted to ACTIVE the moment both are verified.
   */
  async verifyMyEmail(code: string): Promise<void> {
    await request('/me/profile/email/verify', {
      method: 'POST',
      body: { code },
    })
  },

  /** Send the email code again. Also authenticated, same reason. */
  async resendMyEmailCode(): Promise<void> {
    await request('/me/profile/email/resend', { method: 'POST' })
  },

  async requestStudentOtp(tenantSlug: string, phone: string): Promise<void> {
    await request('/auth/student/request-otp', {
      method: 'POST',
      body: { tenant_slug: tenantSlug, phone },
      anonymous: true,
    })
  },

  /** Exchange the code for a session. */
  async verifyStudentOtp(
    tenantSlug: string,
    phone: string,
    code: string,
  ): Promise<TokenResponse> {
    const tokens = await request<TokenResponse>('/auth/student/verify-otp', {
      method: 'POST',
      body: { tenant_slug: tenantSlug, phone, code },
      anonymous: true,
    })
    tokenStore.set(tokens)
    return tokens
  },

  logout(): void {
    tokenStore.clear()
  },

  currentSession(): StoredSession | null {
    return tokenStore.get()
  },
}
